import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  EyeIcon,
  ClipboardDocumentCheckIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  PencilIcon,
  ShareIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const FormDetail = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [submissions, setSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [versions, setVersions] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    // Simulate fetching form data
    const fetchFormData = () => {
      const mockForm = {
        id: parseInt(id),
        title: 'Patient Registration Form',
        description: 'Comprehensive patient registration form for new admissions. This form collects essential patient information including personal details, medical history, insurance information, and emergency contacts.',
        category: 'Patient Forms',
        status: 'active',
        version: '2.1',
        createdDate: '2025-01-20T10:00:00Z',
        lastModified: '2025-01-22T14:30:00Z',
        createdBy: {
          name: 'Dr. Sarah Johnson',
          avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=3b82f6&color=fff'
        },
        responses: 145,
        views: 289,
        fieldCount: 12,
        estimatedTime: '10 minutes',
        isRequired: true,
        tags: ['Registration', 'Patient', 'Medical History'],
        instructions: 'Please fill out all required fields marked with an asterisk (*). Ensure all information is accurate and up-to-date.',
        fields: [
          {
            id: 1,
            type: 'text',
            label: 'Full Name',
            required: true,
            placeholder: 'Enter your full name'
          },
          {
            id: 2,
            type: 'email',
            label: 'Email Address',
            required: true,
            placeholder: 'Enter your email address'
          },
          {
            id: 3,
            type: 'tel',
            label: 'Phone Number',
            required: true,
            placeholder: 'Enter your phone number'
          },
          {
            id: 4,
            type: 'date',
            label: 'Date of Birth',
            required: true
          },
          {
            id: 5,
            type: 'select',
            label: 'Gender',
            required: true,
            options: ['Male', 'Female', 'Other', 'Prefer not to say']
          },
          {
            id: 6,
            type: 'textarea',
            label: 'Medical History',
            required: false,
            placeholder: 'Please describe any relevant medical history'
          },
          {
            id: 7,
            type: 'text',
            label: 'Insurance Provider',
            required: true,
            placeholder: 'Enter your insurance provider'
          },
          {
            id: 8,
            type: 'text',
            label: 'Policy Number',
            required: true,
            placeholder: 'Enter your policy number'
          },
          {
            id: 9,
            type: 'text',
            label: 'Emergency Contact Name',
            required: true,
            placeholder: 'Enter emergency contact name'
          },
          {
            id: 10,
            type: 'tel',
            label: 'Emergency Contact Phone',
            required: true,
            placeholder: 'Enter emergency contact phone'
          },
          {
            id: 11,
            type: 'checkbox',
            label: 'I agree to the terms and conditions',
            required: true
          },
          {
            id: 12,
            type: 'checkbox',
            label: 'I consent to receiving communications',
            required: false
          }
        ]
      };

      const mockSubmissions = [
        {
          id: 1,
          submittedBy: 'John Smith',
          submittedDate: '2025-01-22T15:30:00Z',
          status: 'completed',
          completionTime: '8 minutes'
        },
        {
          id: 2,
          submittedBy: 'Mary Johnson',
          submittedDate: '2025-01-22T14:15:00Z',
          status: 'completed',
          completionTime: '12 minutes'
        },
        {
          id: 3,
          submittedBy: 'Robert Brown',
          submittedDate: '2025-01-22T13:45:00Z',
          status: 'in_progress',
          completionTime: null
        }
      ];

      const mockAnalytics = {
        totalViews: 289,
        totalSubmissions: 145,
        completionRate: 87.5,
        averageTime: '9.2 minutes',
        dropoffPoints: [
          { field: 'Medical History', dropoffRate: 15 },
          { field: 'Insurance Information', dropoffRate: 8 },
          { field: 'Emergency Contact', dropoffRate: 5 }
        ],
        dailyStats: [
          { date: '2025-01-16', views: 12, submissions: 8 },
          { date: '2025-01-17', views: 18, submissions: 14 },
          { date: '2025-01-18', views: 25, submissions: 19 },
          { date: '2025-01-19', views: 31, submissions: 22 },
          { date: '2025-01-20', views: 28, submissions: 18 },
          { date: '2025-01-21', views: 35, submissions: 24 },
          { date: '2025-01-22', views: 42, submissions: 28 }
        ]
      };

      const mockVersions = [
        {
          version: '2.1',
          date: '2025-01-22T14:30:00Z',
          author: 'Dr. Sarah Johnson',
          changes: 'Updated medical history field validation',
          isCurrent: true
        },
        {
          version: '2.0',
          date: '2025-01-20T10:00:00Z',
          author: 'Dr. Sarah Johnson',
          changes: 'Added insurance information fields',
          isCurrent: false
        },
        {
          version: '1.5',
          date: '2025-01-15T16:20:00Z',
          author: 'Jennifer Wu',
          changes: 'Improved form layout and styling',
          isCurrent: false
        },
        {
          version: '1.0',
          date: '2025-01-10T09:00:00Z',
          author: 'Dr. Sarah Johnson',
          changes: 'Initial form creation',
          isCurrent: false
        }
      ];

      setForm(mockForm);
      setSubmissions(mockSubmissions);
      setAnalytics(mockAnalytics);
      setVersions(mockVersions);
      setLoading(false);
    };

    setTimeout(fetchFormData, 500);
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      review: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      active: 'Active',
      draft: 'Draft',
      review: 'Under Review',
      archived: 'Archived'
    };
    return statusLabels[status] || status;
  };

  const getFieldTypeIcon = (type) => {
    switch (type) {
      case 'text':
      case 'email':
      case 'tel':
        return '📝';
      case 'textarea':
        return '📄';
      case 'select':
        return '📋';
      case 'checkbox':
        return '☑️';
      case 'date':
        return '📅';
      default:
        return '📝';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Form not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The form you're looking for doesn't exist or has been removed.
          </p>
          <div className="mt-6">
            <Link
              to="/forms"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeftIcon className="-ml-1 mr-2 h-4 w-4" />
              Back to Forms
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Link
                  to="/forms"
                  className="mr-4 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back to Forms
                </Link>
                <div>
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">{form.title}</h1>
                      <p className="text-sm text-gray-500">Version {form.version}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setIsFavorite(!isFavorite)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {isFavorite ? (
                    <StarIconSolid className="h-4 w-4 text-yellow-400 mr-2" />
                  ) : (
                    <StarIcon className="h-4 w-4 mr-2" />
                  )}
                  {isFavorite ? 'Favorited' : 'Add to Favorites'}
                </button>
                
                <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <ShareIcon className="h-4 w-4 mr-2" />
                  Share
                </button>
                
                <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                  Duplicate
                </button>
                
                <Link
                  to={`/forms/${form.id}/edit`}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </Link>
                
                <Link
                  to={`/forms/${form.id}/fill`}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Fill Form
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Form Status and Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center space-x-4 mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(form.status)}`}>
                  {getStatusLabel(form.status)}
                </span>
                {form.isRequired && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                )}
              </div>
              
              <p className="text-gray-700 mb-4">{form.description}</p>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center text-gray-500">
                  <FolderIcon className="h-4 w-4 mr-2" />
                  {form.category}
                </div>
                <div className="flex items-center text-gray-500">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  {form.estimatedTime}
                </div>
                <div className="flex items-center text-gray-500">
                  <UserIcon className="h-4 w-4 mr-2" />
                  {form.createdBy.name}
                </div>
                <div className="flex items-center text-gray-500">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {formatDate(form.lastModified)}
                </div>
              </div>
              
              {form.tags && (
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {form.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <EyeIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-blue-600">{form.views}</div>
                <div className="text-sm text-blue-600">Total Views</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <ClipboardDocumentCheckIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-600">{form.responses}</div>
                <div className="text-sm text-green-600">Responses</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <DocumentTextIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-purple-600">{form.fields}</div>
                <div className="text-sm text-purple-600">Fields</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <CheckCircleIcon className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-orange-600">{analytics.completionRate}%</div>
                <div className="text-sm text-orange-600">Completion</div>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        {form.instructions && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">Instructions</h3>
                <p className="mt-1 text-sm text-blue-700">{form.instructions}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {[
                { id: 'overview', name: 'Overview', icon: DocumentTextIcon },
                { id: 'fields', name: 'Form Fields', icon: Cog6ToothIcon },
                { id: 'submissions', name: 'Submissions', icon: ClipboardDocumentCheckIcon },
                { id: 'analytics', name: 'Analytics', icon: ChartBarIcon },
                { id: 'versions', name: 'Version History', icon: ClockIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm flex items-center`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Form Description</h3>
                  <p className="text-gray-700">{form.description}</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Key Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500">Completion Rate</div>
                      <div className="text-2xl font-bold text-gray-900">{analytics.completionRate}%</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500">Average Time</div>
                      <div className="text-2xl font-bold text-gray-900">{analytics.averageTime}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500">Response Rate</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {Math.round((form.responses / form.views) * 100)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'fields' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Form Fields ({form.fields.length})</h3>
                <div className="space-y-4">
                  {form.fields.map((field, index) => (
                    <div key={field.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <div className="text-lg mr-3">{getFieldTypeIcon(field.type)}</div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-gray-900">{field.label}</h4>
                              {field.required && (
                                <span className="text-red-500 text-sm">*</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 capitalize">{field.type} field</p>
                            {field.placeholder && (
                              <p className="text-sm text-gray-400 mt-1">
                                Placeholder: "{field.placeholder}"
                              </p>
                            )}
                            {field.options && (
                              <div className="mt-2">
                                <p className="text-sm text-gray-500">Options:</p>
                                <ul className="text-sm text-gray-400 ml-4 list-disc">
                                  {field.options.map((option, optIndex) => (
                                    <li key={optIndex}>{option}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                        <span className="text-sm text-gray-400">#{index + 1}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'submissions' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Recent Submissions</h3>
                  <Link
                    to={`/forms/${form.id}/submissions`}
                    className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                  >
                    View All →
                  </Link>
                </div>
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Submitted By
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Completion Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {submissions.slice(0, 5).map((submission) => (
                        <tr key={submission.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {submission.submittedBy}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(submission.submittedDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              submission.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {submission.status === 'completed' ? 'Completed' : 'In Progress'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {submission.completionTime || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Overview</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-blue-600">Total Views</div>
                      <div className="text-2xl font-bold text-blue-900">{analytics.totalViews}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-green-600">Total Submissions</div>
                      <div className="text-2xl font-bold text-green-900">{analytics.totalSubmissions}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-purple-600">Completion Rate</div>
                      <div className="text-2xl font-bold text-purple-900">{analytics.completionRate}%</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-orange-600">Average Time</div>
                      <div className="text-2xl font-bold text-orange-900">{analytics.averageTime}</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Drop-off Points</h3>
                  <div className="space-y-3">
                    {analytics.dropoffPoints?.map((point, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium text-gray-900">{point.field}</span>
                        <div className="flex items-center">
                          <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                            <div
                              className="bg-red-500 h-2 rounded-full"
                              style={{ width: `${point.dropoffRate}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-500">{point.dropoffRate}% drop-off</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'versions' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Version History</h3>
                <div className="space-y-4">
                  {versions.map((version, index) => (
                    <div key={index} className={`border rounded-lg p-4 ${version.isCurrent ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium text-gray-900">Version {version.version}</h4>
                            {version.isCurrent && (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{version.changes}</p>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <div className="flex items-center">
                              <UserIcon className="h-4 w-4 mr-1" />
                              {version.author}
                            </div>
                            <div className="flex items-center">
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              {formatDate(version.date)}
                            </div>
                          </div>
                        </div>
                        {!version.isCurrent && (
                          <button className="text-blue-600 hover:text-blue-500 text-sm font-medium">
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormDetail;
